/**
 * arrayKeys — how a patch operation addresses an element of a model array.
 *
 * A patch says "change the column called `region`", never "change column 3":
 * index addressing breaks the moment a platform release inserts an element
 * above it. So every array a customer can patch needs a stable per-element key.
 *
 * Most model arrays carry an `id`. A large minority do not, and adding one to
 * all of them would mean ~1800 edits to fields that already have a perfectly
 * good identity under a different name — a `menuItems` entry is identified by
 * its `action`, a dashboard layout cell by its `i`. This module declares those,
 * so the model stays as it reads and the patch engine still gets a key.
 *
 * Resolution order, most specific first:
 *
 *   1. `keyField` given explicitly on the operation
 *   2. a declaration in `arrayKeys` below, matched on the array's path
 *   3. inference over CANDIDATES — first one present on every element and
 *      unique across them
 *   4. none — the array is unkeyed; `where` operations against it are an error
 *      and only `append`, `prepend` and wholesale replacement are legal
 *
 * Step 3 deliberately does NOT consider `name`. In this model `name` is almost
 * always a label — a translation key or a display string — and keying on a
 * label means an array silently loses its identity the moment two elements are
 * labelled the same. Exactly one array in the model ever inferred `name`, and
 * it is declared below by its real key instead.
 *
 * A declaration is a promise that the field is present and unique on every
 * matching array. `npm run lint:arrayKeys` checks that promise against the real
 * model and fails when it stops holding.
 *
 * **A declaration is only acceptable when the field is a genuine identity.**
 * Keying on a *label* (a translation key) or a *category* (`type: 'primary'`,
 * `type: 'ui'`) is not an identity — it happens to be unique today and stops
 * being unique the moment someone adds a second primary button or a second UI
 * effect, at which point a customer's overlay starts matching the wrong element.
 * Five such declarations were retired by giving those elements a real `id`:
 * navigation entries, modal footers, and processView effects. What remains keys on
 * something that genuinely names the element, or on a field a third-party
 * library owns (`i` for react-grid-layout) where adding an `id` would create a
 * second competing identity. `bars → dataKey`, for the Recharts series of the
 * old bar chart widget, went with the last config that used it.
 */

/** Fields tried, in order, when no key is declared. */
export const CANDIDATES = ['id', 'key', 'path']

/**
 * Declared keys. `path` matches the TRAILING segments of an array's path, with
 * `[]` standing for an array-element step and `*` for exactly one segment. The
 * longest match wins, so a qualified pattern beats a bare property name.
 *
 * `ns` says which model the declaration belongs to — 'client' (the default) or
 * 'server'. Each linter walks one model and checks only its own declarations, so
 * a server declaration is not reported as stale by the client.
 */
export const arrayKeys = [
    // --- identified by the action they dispatch ----------------------------
    { path: 'menuItems', keyField: 'action' },

    // --- page view routing --------------------------------------------------
    { path: 'filterArray', keyField: 'filterValue' },

    // --- dashboard grid: `i` is react-grid-layout's own element id ----------
    { path: 'layouts.*', keyField: 'i' },
    { path: 'widgets', keyField: 'i' },

    // --- Postgres indexes: exactly one of these names each entry ------------
    // 397 entries across 69 arrays, already unique — a declaration rather than
    // 397 ids restating a column name.
    //
    // `columns` indexes promoted physical columns rather than a path into the
    // JSONB blob, so it names an entry the same way the other three do. It was
    // missing here for as long as nothing used it; ledgerEntry does, and every
    // one of its entries then keyed to undefined and collided.
    { path: 'indexesSupabase', keyField: ['json', 'jsonComposite', 'jsonGin', 'columns'], ns: 'server' },

    // --- collapsed display panels: `value` is the field being displayed,
    // except a `dualPill` (a debit/credit pair rendered as one pill), which
    // has no single `value` and is identified by `debitField` instead -------
    { path: 'displaySection.columns[].elements', keyField: ['value', 'debitField'] },
    { path: 'subtitle', keyField: 'value' },

    // --- list filter bar: identified by the field each one filters on --------
    { path: 'groupFilters.filters', keyField: 'filterField' },

]

// ---------------------------------------------------------------------------

/** Split a path into segments, turning `foo[2]` into `foo`, `[]`. */
export function pathSegments(path) {
    const out = []
    for (const raw of String(path).split('.')) {
        if (!raw) continue
        const m = /^([^[]*)((?:\[[^\]]*\])*)$/.exec(raw)
        if (!m) { out.push(raw); continue }
        if (m[1]) out.push(m[1])
        for (const _ of m[2].match(/\[[^\]]*\]/g) ?? []) out.push('[]')
    }
    return out
}

/**
 * The declaration matching an array at `path`, or null.
 * Matches on trailing segments; the longest matching declaration wins.
 */
export function matchDeclaration(path) {
    if (!path) return null
    const segs = pathSegments(path)
    let best = null
    let bestLen = -1
    for (const entry of arrayKeys) {
        const pat = pathSegments(entry.path)
        if (pat.length > segs.length || pat.length <= bestLen) continue
        let ok = true
        for (let i = 0; i < pat.length; i++) {
            const seg = segs[segs.length - pat.length + i]
            if (pat[i] === '*' ? seg === '[]' : seg !== pat[i]) { ok = false; break }
        }
        if (ok) { best = entry; bestLen = pat.length }
    }
    return best
}

/** The declared key field for an array at `path`, or null. */
export function declaredKeyFor(path) {
    return matchDeclaration(path)?.keyField ?? null
}

/**
 * Which of a declaration's fields actually names THIS element.
 *
 * A `keyField` is usually one name. It may be a list, for arrays whose elements
 * are identified by different fields depending on their kind — a Postgres index
 * is `{ json }`, `{ jsonComposite }` or `{ jsonGin }`, never more than one. The
 * first field present wins, so the element is addressed by the field that
 * actually describes it: `indexesSupabase[json=name]`.
 */
export function keyFieldOf(el, field) {
    if (!el || typeof el !== 'object' || Array.isArray(el)) return null
    for (const f of (Array.isArray(field) ? field : [field])) {
        const v = el[f]
        if (v !== undefined && v !== null) return f
    }
    return null
}

/**
 * The comparable identity of one element under a declaration.
 * Composite values (an array of columns) are compared by shape, and the field
 * name is part of the identity so `{json:'a'}` and `{jsonComposite:['a']}` are
 * two different indexes rather than a collision.
 */
function keyIdentity(el, field) {
    const f = keyFieldOf(el, field)
    if (f === null) return undefined
    const v = el[f]
    if (Array.isArray(v)) return `${f}=${JSON.stringify(v)}`
    const t = typeof v
    if (t !== 'string' && t !== 'number' && t !== 'boolean') return undefined
    // A single-field declaration keeps the bare value, so canonical paths stay
    // `columns[id=name]` rather than growing a redundant prefix.
    return Array.isArray(field) ? `${f}=${v}` : v
}

/** True when `field` names every element and does so uniquely. */
export function isUsableKey(array, field) {
    if (!Array.isArray(array) || array.length === 0) return false
    const seen = new Set()
    for (const el of array) {
        const identity = keyIdentity(el, field)
        if (identity === undefined) return false
        if (seen.has(identity)) return false
        seen.add(identity)
    }
    return true
}

/**
 * Resolve the key field for an array, following the four steps above.
 * Returns null when the array is unkeyed.
 *
 * `override` is the operation's explicit `keyField`. It is returned even when
 * unusable — the caller reports that as a diagnostic against the patch, rather
 * than silently falling back to a different field than the author asked for.
 */
export function arrayKeyFor(array, path, override) {
    if (override) return override
    const declared = declaredKeyFor(path)
    if (declared && isUsableKey(array, declared)) return declared
    for (const candidate of CANDIDATES) {
        if (isUsableKey(array, candidate)) return candidate
    }
    return null
}
